import { createContext, FC, useContext, useEffect, useState } from "react";
import { InitMessage } from "../../libs/react-components/InitMessage";
import CoreApiService from "../../system/api/coreApiService";
import renderGlobalAlert from "../../system/hooks/useGlobalAlert";
import ReportApiService from "../api/reportApiService";
import UIApiService from "../api/uiApiService";
import { envF } from "../../env";

export interface IAppContext {
    uiApiService: UIApiService;
    coreApiService: CoreApiService;
    reportAPIService: ReportApiService;
}

const defaultAppContext: IAppContext = {
    uiApiService: new UIApiService(),
    coreApiService: new CoreApiService(""),
    reportAPIService: new ReportApiService("", new CoreApiService("")),
};

const AppContext = createContext<IAppContext>(defaultAppContext);
const useAppContext = () => useContext(AppContext);

const AppContextProvider: FC = (props) => {
    const [componentState] = useState<ComponentStateType>("Initialized");
    const baseUrl = envF.REACT_APP_API_GATEWAY_PATH as string;

    const [coreApiService] = useState(() => new CoreApiService(baseUrl));
    const [reportApiService] = useState(() => new ReportApiService(baseUrl, new CoreApiService(baseUrl)));
    const [uiApiService] = useState(() => new UIApiService());

    useEffect(() => {
        const unsubscribe = coreApiService.subscribeOnGetResponse((e) => {
            if (e.respType === "isCompleted") {
                if (e.message.isSilent) {
                    renderGlobalAlert({
                        variant: e.message.type,
                        statusCode: e.message.statusCode,
                        title: 'Сохранено ' + e.message.text,
                    });
                }
            } else {
                const shouldShowAlert = e.message.title
                // Игнорируем ошибки "не найдена" только для contractor_motivation
                const isContractorMotivationNotFound = 
                    e.message.instance?.includes('/ContractorMotivation/') && 
                    (e.message.title?.includes('не найдена') || e.message.detail?.includes('не найдена'));
                if (shouldShowAlert && !isContractorMotivationNotFound) {
                    renderGlobalAlert({
                        variant: e.message.type,
                        statusCode: e.message.statusCode ?? 500,
                        title: e.message.title,
                        detail: e.message.detail,
                        instance: e.message.instance,
                    });
                }
            }
        });

        // Функция очистки, если subscribeOnGetResponse возвращает функцию отписки
        return () => {
            // Проверяем тип перед вызовом
            if (typeof unsubscribe === 'function') {
                (unsubscribe as Function)();
            }
        };
    }, [coreApiService]);

    const renderProvider = () => (
        <AppContext.Provider value={{ coreApiService, uiApiService, reportAPIService: reportApiService }}>
            {props.children}
        </AppContext.Provider>
    );

    switch (componentState) {
        case "Initializing": {
            return <InitMessage>Инициализация приложения F3Tail Web</InitMessage>;
        }
        case "Failed": {
            return <div>Failed</div>;
        }
        case "Initialized": {
            return renderProvider();
        }
        default: {
            return null;
        }
    }
};

export default AppContextProvider;
export { useAppContext, AppContext };